import React from 'react';
import {List, useRedirect, useTranslate} from 'react-admin';
import {Box, Typography} from '@mui/material';
import {t} from '../themes/statamicTokens';
import StatamicListPage from '../components/statamic/StatamicListPage';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';

const formatDateTime = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const TextCell = ({value}) => (
    <Typography
        sx={{
            fontSize: `${t.type.body.size}px`,
            color: value ? t.color.text.secondary : t.color.text.muted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        }}
    >
        {value ?? '—'}
    </Typography>
);

const BoolDot = ({value, label}) => (
    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
        <StatusDot variant={value ? 'success' : 'neutral'}/>
        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
            {label}
        </Typography>
    </Box>
);

const MailLogsListStatamicInner = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    const columns = [
        {
            key: 'mail_type', label: 'Mail type', sortable: true, sortField: 'mail_type', width: 180,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: t.type.bodyStrong.weight, color: t.color.text.primary, whiteSpace: 'nowrap'}}>
                    {r.mail_type || '—'}
                </Typography>
            ),
        },
        {
            key: 'recipient', label: translate('Guardian name'), width: 220,
            render: (r) => <TextCell value={r.recipient}/>,
        },
        {
            key: 'charity', label: translate('Charity'), width: 180,
            render: (r) => <TextCell value={r.charity}/>,
        },
        {
            key: 'child', label: translate('Child name'), width: 180,
            render: (r) => <TextCell value={r.child}/>,
        },
        {
            key: 'sent', label: translate('Date sent'), sortable: true, sortField: 'sent_date_time', width: 170,
            render: (r) => <TextCell value={formatDateTime(r.sent_date_time)}/>,
        },
        {
            key: 'success', label: translate('Sent successfully'), width: 110,
            render: (r) => r.success === true || r.success === 'true'
                ? <StatusPill variant="success">Sent</StatusPill>
                : r.success === false || r.success === 'false'
                    ? <StatusPill variant="danger">Failed</StatusPill>
                    : <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>—</Typography>,
        },
        {
            key: 'read', label: translate('Read'), width: 90,
            render: (r) => <BoolDot value={!!r.read} label={r.read ? translate('Read') : 'No'}/>,
        },
        {
            key: 'clicked', label: translate('Clicked'), width: 90,
            render: (r) => <BoolDot value={!!r.clicked} label={r.clicked ? translate('Clicked') : 'No'}/>,
        },
    ];

    return (
        <StatamicListPage
            title={translate('Mail Logs')}
            subtitle={translate('Outbound mail history and delivery status')}
            columns={columns}
            onRowClick={(record) => redirect('show', 'mailLogs', record.id)}
        />
    );
};

const MailLogsListStatamic = (props) => (
    <List
        {...props}
        sort={{field: 'id', order: 'DESC'}}
        perPage={25}
        actions={false}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <MailLogsListStatamicInner/>
    </List>
);

export default MailLogsListStatamic;
